import { Button, notification } from "antd"
import { type ReactNode } from "react"

import {
  type NotificationType,
  notificationOptions
} from "../../constants/notification.config"
import {
  ERROR_MESSAGE_KEYS,
  isErrorMessageKey
} from "../../constants/error-messages"
import {
  type HandledError,
  type HandledErrorAction,
  isHandledError
} from "../../errors/handled-error"
import { presentHandledError } from "../../errors/present-handled-error"
import i18n from "../../i18n"

export type HandledErrorActionHandler = (action: HandledErrorAction) => void

const HANDLED_ERROR_TOAST_KEY = "handled_error_notification"

export const unknownError = ERROR_MESSAGE_KEYS.unknown

const translateError = (key: string): string => i18n.t(key) as string

const isTransportError = (error: any): boolean =>
  !error?.response &&
  (error?.isAxiosError === true ||
    error?.code === "ERR_NETWORK" ||
    error?.message === "Network Error")

/**
 * Pulls a safe, display-ready string out of a rejected thunk's raw payload
 * — never the payload itself. `rest_framework_custom_exception_handler`
 * always wraps a backend error as `{message: {detail: "..."}, details:
 * {message: "...", ...}, ...}`, so `payload.message` is an OBJECT, not a
 * string, despite call sites that cast it `as {message?: string}`. Passing
 * that object straight into an antd `Alert`/`message` prop crashes the
 * whole page with React error #31 ("Objects are not valid as a React
 * child") — confirmed as the real cause of a production crash on
 * Settings → Branches → Add branch, where a 403's `{detail: "..."}` shape
 * reached exactly that render path.
 */
export function extractErrorMessage(
  payload: unknown,
  fallback: string
): string {
  if (typeof payload === "string") {
    return isErrorMessageKey(payload) ? translateError(payload) : payload
  }
  if (payload && typeof payload === "object") {
    const details = (payload as any).details
    if (
      details &&
      typeof details === "object" &&
      typeof details.message === "string"
    ) {
      return details.message
    }
    const message = (payload as any).message
    if (typeof message === "string") return message
    if (
      message &&
      typeof message === "object" &&
      typeof message.detail === "string"
    ) {
      return message.detail
    }
    const detail = (payload as any).detail
    if (typeof detail === "string") return detail
  }
  return fallback
}

function getErrorMessage(error: any) {
  if (!error) {
    return { message: translateError(ERROR_MESSAGE_KEYS.unknown) }
  }

  // Handle string errors
  if (typeof error === "string") {
    return {
      message: isErrorMessageKey(error) ? translateError(error) : error
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    if (isTransportError(error)) {
      return { message: translateError(ERROR_MESSAGE_KEYS.network) }
    }
    return { message: error?.message }
  }

  // Handle new format
  if (typeof error === "object" && error?.details) {
    const details = error.details

    // Check if it's a new structure
    if (details?.message || details?.description) {
      return {
        message:
          (isErrorMessageKey(details?.message)
            ? translateError(details.message)
            : details?.message) || translateError(ERROR_MESSAGE_KEYS.unknown),
        description: details?.description || null,
        type: details?.type || "error"
      }
    }
  }

  // Handle objects with message property
  if (typeof error === "object" && error?.message) {
    if (isTransportError(error)) {
      return { message: translateError(ERROR_MESSAGE_KEYS.network) }
    }

    if (typeof error?.message === "string") {
      if (error?.message === "Validation error") {
        const excludedKeys = [
          "message",
          "type",
          "description",
          "data",
          "status_code"
        ]
        const messages: Record<string, string | ReactNode>[] = []

        // Рекурсивна функція для обробки вкладених помилок валідації
        const extractValidationErrors = (obj: any, parentKey = "") => {
          for (const key in obj) {
            const value = obj[key]
            const currentPath = parentKey ? `${parentKey}.${key}` : key

            if (excludedKeys.includes(key)) continue

            // Масив? Перевіряємо перший елемент
            if (Array.isArray(value) && value.length > 0) {
              const first = value?.[0]

              // Якщо це рядок — додаємо помилку
              if (typeof first === "string") {
                messages.push({
                  key: `error_${currentPath}`,
                  message: (
                    <span className="lowercase">
                      {key} - {first}
                    </span>
                  ),
                  type: "warning"
                })
              }
              // Якщо це не рядок — можливо, вкладений об’єкт → рекурсія
              else if (typeof first === "object" && first !== null) {
                extractValidationErrors(first, currentPath)
              }

              continue
            }

            // Якщо value — об’єкт, обробляємо далі
            if (typeof value === "object" && value !== null) {
              extractValidationErrors(value, currentPath)
            }
          }
        }

        if (error?.details) {
          extractValidationErrors(error.details)
        } else {
          extractValidationErrors(error)
        }

        if (messages.length > 0) {
          return { messages }
        }
      }

      return {
        message: isErrorMessageKey(error.message)
          ? translateError(error.message)
          : error.message,
        type: error?.type
      }
    }

    // Handle nested object messages
    if (typeof error?.message === "object") {
      const messages: Record<string, string>[] = []
      for (const key in error.message) {
        if (key === "type") continue // Skip type field

        if (key === "detail" && typeof error?.message?.[key] !== "object") {
          messages.push({
            key: `error_${key}`,
            message: error?.message?.[key],
            type: error?.message?.type || "error"
          })
        } else if (typeof error?.message?.[key] === "string") {
          messages.push({
            key: `error_${key}`,
            message: `${key}: ${error?.message?.[key]}`,
            type: error?.message?.type || "error"
          })
        }
      }

      if (messages?.length > 0) {
        return { messages }
      } else {
        return { message: translateError(ERROR_MESSAGE_KEYS.backend) }
      }
    }
  }

  // Handle plain objects (fallback)
  if (typeof error === "object") {
    const messages: Record<string, string>[] = []
    for (const key in error) {
      messages.push({
        key: `error_${key}`,
        message: key,
        description: error?.[key],
        type: error?.type || "error"
      })
    }

    if (messages.length > 0) {
      return { messages }
    }
  }

  if (error?.message) {
    return { message: error?.message, type: error?.type }
  }

  return { message: translateError(ERROR_MESSAGE_KEYS.unknown) }
}

const translateWithOptions = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, options) as string

function showHandledErrorNotification(
  error: HandledError,
  toast: any,
  onAction?: HandledErrorActionHandler
) {
  const presented = presentHandledError(error, translateWithOptions)
  const handler = toast?.[presented.type] || toast?.["error"]
  const action = error.action

  handler({
    ...(notificationOptions?.[presented.type] || notificationOptions.error),
    message: presented.message,
    description: presented.description,
    key: HANDLED_ERROR_TOAST_KEY,
    ...(action && presented.actionLabel
      ? {
          duration: 0,
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                toast?.destroy?.(HANDLED_ERROR_TOAST_KEY)
                if (onAction) onAction(action)
                else window.location.assign(action.href)
              }}
            >
              {presented.actionLabel}
            </Button>
          )
        }
      : {})
  })
}

export function showErrorNotifications(
  error: any,
  appNotification?: any,
  onAction?: HandledErrorActionHandler
) {
  if (isHandledError(error)) {
    showHandledErrorNotification(
      error,
      appNotification || notification,
      onAction
    )
    return
  }

  if (
    error?.code === "token_not_valid" ||
    (typeof error?.message === "string" &&
      error.message.includes("Token is expired"))
  ) {
    return
  }

  const errorData = getErrorMessage(error)
  const toast = appNotification || notification

  if (errorData?.messages) {
    // Multiple error messages
    errorData.messages.forEach((item) => {
      const notificationType = (item?.type || "error") as NotificationType
      const handler = toast?.[notificationType] || toast?.["error"]

      handler({
        ...notificationOptions?.[notificationType],
        message: item?.message,
        description: item?.description,
        key: item?.key
      })
    })
  } else {
    // Single error message (including new format)
    const notificationType = (errorData?.type || "error") as NotificationType
    const handler = toast?.[notificationType] || toast?.["error"]

    handler({
      ...(notificationOptions?.[notificationType] || notificationOptions.error),
      message: errorData?.message,
      description: errorData?.description,
      key: "error_notification"
    })
  }
}
